import { useState } from 'react'
import { Hospital } from '../types'
import useSWR, { mutate } from 'swr'
import { fetcher, apiRequest } from '../utils/api'

function HospitalManager() {
  const [showForm, setShowForm] = useState(false)
  const [editingHospital, setEditingHospital] = useState<Hospital | null>(null)
  const [formData, setFormData] = useState({ name: '', address: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: hospitals, error: hospitalsError } = useSWR<Hospital[]>('/hospitals', fetcher)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const url = editingHospital ? `/hospitals/${editingHospital.id}` : '/hospitals'
      const method = editingHospital ? 'PATCH' : 'POST'
      
      await apiRequest(url, {
        method,
        body: JSON.stringify(formData),
      })

      mutate('/hospitals')
      setFormData({ name: '', address: '' })
      setShowForm(false)
      setEditingHospital(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hospital')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (hospital: Hospital) => {
    setEditingHospital(hospital)
    setFormData({ name: hospital.name, address: hospital.address })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this hospital?')) return

    try {
      await apiRequest(`/hospitals/${id}`, {
        method: 'DELETE',
      })

      mutate('/hospitals')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete hospital')
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingHospital(null)
    setFormData({ name: '', address: '' })
    setError(null)
  }

  if (hospitalsError) {
    return <div className="alert alert-error">Failed to load hospitals</div>
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Hospital Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add Hospital
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
          <h3>{editingHospital ? 'Edit Hospital' : 'Add New Hospital'}</h3>
          
          <div className="form-group">
            <label>Hospital Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
            />
          </div>

          <div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : editingHospital ? 'Update' : 'Add'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} style={{ marginLeft: '1rem' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Address</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {hospitals?.map((hospital) => (
            <tr key={hospital.id}>
              <td>{hospital.name}</td>
              <td>{hospital.address}</td>
              <td>{new Date(hospital.created_at).toLocaleDateString()}</td>
              <td>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleEdit(hospital)}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDelete(hospital.id)}
                >
                  Delete
                </button>
                <a 
                  href={`/api/qr/print/${hospital.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success"
                >
                  Print QR Codes
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!hospitals?.length && (
        <p>No hospitals found. Add one to get started.</p>
      )}
    </div>
  )
}

export default HospitalManager